// The chat transcript reducer (beekeeper-agent-chat, `client-beekeeper-chat`): pure, synchronous,
// and framework-free — it owns no I/O, so `chat-channel.ts` is the only thing that ever calls it,
// and everything downstream of the overlay reads from its output rather than reaching into raw
// `TurnEventDto`s itself. Kept separate from UI policy on purpose: which tool rows auto-expand and
// how a terminal reason is worded live in the `ui/components/chat/` components, not here — this
// module states only what happened, never how to draw it.
//
// The idempotent-replay guard (`cursorByTurn`) is what makes 7.15/7.16 possible: a dropped
// connection reattaches from the last received cursor, and any event the server re-delivers below
// that cursor is a no-op here rather than a duplicated message or tool row.

import type { ThreadDto, TurnEventDto } from '../../net/chat-sse';

export interface ToolRowView {
  callId: string;
  tool: string;
  status: 'running' | 'ok' | 'error' | 'skipped';
  /** Derived from the tool's OWN result — never a model paraphrase (client-beekeeper-chat). Empty
   * while `status === 'running'`. */
  summary: string;
  detail: string;
}

export interface MessageView {
  id: string;
  role: 'player' | 'agent';
  text: string;
  toolCalls: ToolRowView[];
}

export interface TerminalRowModel {
  turnId: string;
  /** The wire's raw reason string — deliberately untyped beyond `string`: a reason this client does
   * not recognise (the follow-on `phase_ended`, or anything else) must still carry through render
   * rather than being rejected at the model layer. */
  reason: string;
  version: number | null;
}

export type TranscriptItem =
  | { kind: 'message'; message: MessageView }
  | { kind: 'tool'; tool: ToolRowView }
  | { kind: 'terminal'; terminal: TerminalRowModel };

export interface TranscriptState {
  items: TranscriptItem[];
  /** The agent message item currently accreting this turn's text/tool-call events, or `null` when
   * none is open (before the first delta/tool call of a turn, or after `turnFinished`/a fresh
   * player message closed the previous one). */
  openAgentMessageId: string | null;
  /** The highest cursor already applied, per turn — the idempotent-replay guard. */
  cursorByTurn: Record<string, number>;
  running: boolean;
  /** The most recently started turn's id, kept after it finishes so a "Stop" press racing a
   * just-arrived `turnFinished` has nothing left to call, and so a terminal row's turn is always
   * nameable. `null` before any turn has ever started. */
  currentTurnId: string | null;
}

export const EMPTY_TRANSCRIPT: TranscriptState = {
  items: [],
  openAgentMessageId: null,
  cursorByTurn: {},
  running: false,
  currentTurnId: null,
};

/** Appends the player's own message and closes whatever agent message was open — a fresh player
 * message always starts a new section, never merges into a still-open agent reply from a moment
 * ago (there is no such thing mid-turn, since the overlay disables the input while running, but a
 * reopened-thread's history could otherwise look ambiguous without this). */
export function appendPlayerMessage(state: TranscriptState, id: string, text: string): TranscriptState {
  return {
    ...state,
    openAgentMessageId: null,
    items: [...state.items, { kind: 'message', message: { id, role: 'player', text, toolCalls: [] } }],
  };
}

/**
 * @param cursor The event opening this message, if one is being opened — its own monotonic wire
 * cursor becomes the new message's id (E2E round-1 finding M-1, 2026-08-09), never `items.length`.
 * A length-derived id repeats across replays/reattaches that leave the array a different shape
 * than the one that minted an earlier id, which is exactly the "an id that repeats" the finding
 * calls out — a cursor that already passed the idempotent-replay guard above cannot.
 */
function withOpenAgentMessage(
  state: TranscriptState,
  turnId: string,
  cursor: number,
): { items: TranscriptItem[]; id: string } {
  if (state.openAgentMessageId !== null) {
    return { items: state.items, id: state.openAgentMessageId };
  }
  const id = `msg-${turnId}-${cursor}`;
  const message: MessageView = { id, role: 'agent', text: '', toolCalls: [] };
  return { items: [...state.items, { kind: 'message', message }], id };
}

function mapMessage(
  items: TranscriptItem[],
  id: string,
  fn: (m: MessageView) => MessageView,
): TranscriptItem[] {
  return items.map((item) =>
    item.kind === 'message' && item.message.id === id ? { kind: 'message', message: fn(item.message) } : item,
  );
}

/** Applies one decoded `TurnEventDto` to `state`, returning the next state. Idempotent: an event
 * whose `cursor` is not strictly greater than the highest already-applied cursor for its `turnId`
 * is a no-op — the reattach/replay guard 7.15/7.16 need. */
export function applyTurnEvent(state: TranscriptState, event: TurnEventDto): TranscriptState {
  const highestSeen = state.cursorByTurn[event.turnId] ?? 0;
  if (event.cursor <= highestSeen) {
    return state;
  }
  const cursorByTurn = { ...state.cursorByTurn, [event.turnId]: event.cursor };

  switch (event.kind) {
    case 'turnStarted': {
      // **Adopt the server's id for the player message that opened this turn**, where it sends one.
      // The send leg had nowhere to carry it (the response *is* the SSE stream), so the message was
      // appended under a local `player-N` id; renaming it here is what stops a just-sent prompt being
      // unresolvable until the next thread fetch. Absent — which is every deployment today — the local
      // id stands and this is a no-op.
      return {
        ...state,
        cursorByTurn,
        running: true,
        currentTurnId: event.turnId,
        openAgentMessageId: null,
      };
    }

    case 'textDelta': {
      const { items, id } = withOpenAgentMessage(state, event.turnId, event.cursor);
      return {
        ...state,
        cursorByTurn,
        openAgentMessageId: id,
        items: mapMessage(items, id, (m) => ({ ...m, text: m.text + (event.text ?? '') })),
      };
    }

    // Additive (E2E round-1 finding M-1): a COMPLETE, standalone assistant utterance for one model
    // call, never a fragment to append — `textDelta` owns incremental streaming, this is its
    // discrete-message sibling, and a canned backend re-saying its whole line once per model call
    // within one turn is exactly what this exists for (`chat-sse.ts`'s own note on the kind).
    // Always closing out whatever was open and opening a fresh message is what keeps two such
    // frames from smearing into one bubble that repeats the same sentence, and keying the new
    // message off the event's own cursor is what keeps its key from ever coinciding with a
    // sibling's — the same guarantee `withOpenAgentMessage` now gives `textDelta`/`toolStarted`.
    case 'assistantMessage': {
      const id = `msg-${event.turnId}-${event.cursor}`;
      const message: MessageView = { id, role: 'agent', text: event.text ?? '', toolCalls: [] };
      return {
        ...state,
        cursorByTurn,
        // Closed, not left open: a following toolStarted/textDelta belongs to the NEXT model call
        // and must open its own message rather than attaching to this one's already-complete text.
        openAgentMessageId: null,
        items: [...state.items, { kind: 'message', message }],
      };
    }

    case 'toolStarted': {
      const row: ToolRowView = {
        callId: event.callId ?? '',
        tool: event.tool ?? '',
        status: 'running',
        summary: '',
        detail: '',
      };
      return {
        ...state,
        cursorByTurn,
        // A tool is an item-level chronology boundary: later prose must open a fresh segment.
        openAgentMessageId: null,
        items: [...state.items, { kind: 'tool', tool: row }],
      };
    }

    case 'toolFinished': {
      const items = state.items.map((item) => {
        if (item.kind !== 'tool' || item.tool.callId !== event.callId) {
          return item;
        }
        return {
          kind: 'tool' as const,
          tool: {
            ...item.tool,
            status: event.status ?? 'ok',
            summary: event.summary ?? '',
            detail: event.detail ?? '',
          },
        };
      });
      return { ...state, cursorByTurn, items };
    }

    case 'turnFinished':
      return {
        ...state,
        cursorByTurn,
        running: false,
        openAgentMessageId: null,
        items: [
          ...state.items,
          {
            kind: 'terminal',
            terminal: {
              turnId: event.turnId,
              reason: event.reason ?? 'completed',
              version: event.version ?? null,
            },
          },
        ],
      };
  }
}

/** The `PromptBox` re-entry row's derived summary (`client-beekeeper-chat` "A separate control
 * opens an existing thread without sending"; sketch `promptbox-entry.html`'s "Idle, with a thread
 * to return to" state). `null` when there is nothing to reopen yet (no messages at all). */
export interface ThreadSummary {
  /** The most recent agent line, or the last message's text if the agent has not spoken yet. */
  lastLine: string;
  messageCount: number;
  /** The version cut by the most recent terminal turn that changed the document, or `null`. */
  lastVersion: number | null;
  /** Mirrors `running` — the sketch's "Stopped partway — 10 steps used" busy treatment. */
  busy: boolean;
  /**
   * The newest thing the AGENT has done — its latest line, or the tool row it is on — flattened to
   * one line, or `null` if it has done nothing yet (`player-chat-and-outcome` **D-3**).
   *
   * Distinct from `lastLine`, which answers "what does this thread say" for a settled conversation.
   * This answers "what is happening right now", which is the question a minimised overlay leaves
   * the re-entry row holding. Tool rows count: "Reading the harvester program" is a truer report of
   * a live turn than the last thing the agent managed to say before it started working.
   *
   * A tool contributes its **own summary**, which is empty while it runs — so a tool in flight adds
   * nothing here and the line stays on whatever the agent last said. That is deliberate: naming a
   * running tool would mean a display label, and how a tool name is worded is UI policy that lives
   * in `ui/components/chat/`, never in this module (see the file header).
   */
  activityLine: string | null;
}

/** The re-entry row shows prose, not markup: agent text is markdown (`AgentProse` renders it in
 * the overlay), but the one-line preview must read as a sentence — so syntax is flattened rather
 * than rendered. Deliberately a light regex pass, not a parser: the row truncates to one line, so
 * fidelity past "no visible `**`/backticks/fences" buys nothing. */
function flattenMarkdown(text: string): string {
  return text
    .replace(/```[^`]*```/gs, ' ') // fenced blocks read as noise at one line — drop them whole
    .replace(/`([^`]*)`/g, '$1')
    .replace(/\*\*([^*]*)\*\*/g, '$1')
    .replace(/\*([^*]*)\*/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** The newest agent-authored line in the thread — a message's prose or a settled tool's own
 * summary, whichever came last. Walks forward and keeps the latest rather than walking backward and
 * stopping, because an agent message carries its tool rows INSIDE it (`MessageView.toolCalls`) as
 * well as beside it, so "the last item" is not the same question as "the last thing that happened".
 * `null` when the agent has produced nothing yet. */
function latestAgentActivity(state: TranscriptState): string | null {
  let latest: string | null = null;
  const consider = (text: string): void => {
    const flat = flattenMarkdown(text);
    if (flat.length > 0) {
      latest = flat;
    }
  };
  for (const item of state.items) {
    if (item.kind === 'tool') {
      consider(item.tool.summary);
    } else if (item.kind === 'message' && item.message.role === 'agent') {
      consider(item.message.text);
      for (const call of item.message.toolCalls) {
        consider(call.summary);
      }
    }
  }
  return latest;
}

export function summarizeThread(state: TranscriptState): ThreadSummary | null {
  const messages = state.items.filter(
    (item): item is Extract<TranscriptItem, { kind: 'message' }> => item.kind === 'message',
  );
  if (messages.length === 0) {
    return null;
  }
  const lastAgent = [...messages].reverse().find((item) => item.message.role === 'agent');
  const lastTerminal = [...state.items]
    .reverse()
    .find((item): item is Extract<TranscriptItem, { kind: 'terminal' }> => item.kind === 'terminal');
  return {
    lastLine: flattenMarkdown((lastAgent ?? messages[messages.length - 1]!).message.text),
    messageCount: messages.length,
    lastVersion: lastTerminal?.terminal.version ?? null,
    busy: state.running,
    activityLine: latestAgentActivity(state),
  };
}

/** Builds the initial transcript from a fetched {@link ThreadDto} — the "reopen an existing thread
 * without sending" path (client-beekeeper-chat) and the rehydration seed for a page load
 * (`inFlight` is what tells the caller to re-attach rather than render a finished transcript). Turn
 * rows are appended after every message: the wire carries no per-message `turnId` to interleave
 * them precisely, so this renders the full history in the two orders the DTO actually provides
 * rather than inventing a join the contract does not carry.
 *
 * **An in-flight turn is left out of `cursorByTurn` deliberately, so its log replays from 0.** The
 * server writes a turn's agent text and tool evidence into the thread only when the turn *settles*
 * — mid-turn the body carries the player's own message and nothing else, however far
 * `inFlight.cursor` has advanced. That cursor is therefore a **progress report, not a resume
 * point**: seeding the guard from it would make the reducer discard the very replay that fills the
 * hole, permanently losing every delta and tool row emitted before the reload (and leaving a later
 * `toolFinished` with no row to land on). Replaying from the start is sound precisely because the
 * body holds none of it and because re-delivery is idempotent anyway. A *dropped connection* is
 * the opposite case and keeps its own cursor — that one the client genuinely holds. */
export function transcriptFromThread(dto: ThreadDto): TranscriptState {
  if (dto.items !== undefined) {
    return {
      items: dto.items.map((item) => {
        if (item.kind === 'message') {
          return {
            kind: 'message' as const,
            message: { id: item.id, role: item.role, text: item.text, toolCalls: [] },
          };
        }
        if (item.kind === 'tool') {
          return { kind: 'tool' as const, tool: { ...item } };
        }
        return {
          kind: 'terminal' as const,
          terminal: { turnId: item.turnId, reason: item.reason, version: item.version },
        };
      }),
      openAgentMessageId: null,
      cursorByTurn: {},
      running: dto.inFlight !== null,
      currentTurnId: dto.inFlight?.turnId ?? null,
    };
  }
  const items: TranscriptItem[] = dto.messages.map((m) => ({
    kind: 'message',
    message: {
      id: m.id,
      role: m.role,
      text: m.text,
      toolCalls: (m.toolCalls ?? []).map((t) => ({
        callId: t.callId,
        tool: t.tool,
        status: t.status,
        summary: t.summary,
        detail: t.detail,
      })),
    },
  }));
  for (const turn of dto.turns) {
    items.push({ kind: 'terminal', terminal: { turnId: turn.turnId, reason: turn.reason, version: turn.version } });
  }
  return {
    items,
    openAgentMessageId: null,
    cursorByTurn: {},
    running: dto.inFlight !== null,
    currentTurnId: dto.inFlight?.turnId ?? null,
  };
}
